// Hilfen für das Prompt-Modul (SPEC 4.3): Anzeige von Phase, Status, Score, Kosten. Rein, ohne Netzwerk.
import type { PromptBriefing, PromptFunnel, PromptItem, PromptRequirements, PromptRun, PromptStatus, PromptTopic } from "./api/types";
import { formatCount, formatDate, formatEuro } from "./format";

export const FUNNEL_LABEL: Record<PromptFunnel, string> = {
  awareness: "Awareness",
  consideration: "Consideration",
  decision: "Decision",
  retention: "Retention",
};

export const STATUS_LABEL: Record<PromptStatus, string> = {
  vorschlag: "Vorschlag",
  freigegeben: "Freigegeben",
  verworfen: "Verworfen",
};

export const STATUS_ORDER: PromptStatus[] = ["vorschlag", "freigegeben", "verworfen"];

export const KIND_LABEL = { branded: "branded", unbranded: "unbranded" } as const;

export const BRIEFING_SOURCES = ["briefing", "fanout", "cluster"] as const;

/** Maximal so viele Prompts je Trigger-Test (Backend: `MAX_PROMPTS`). */
export const TRIGGER_MAX = 20;
/** Plattformen im Trigger-Test (DataForSEO). */
export const TRIGGER_PLATFORMS = 2;

/** Score als Zahl mit Stufe als Text. Die Farbe ist nur Zusatz. */
export function scoreTone(score: number | null): { label: string; className: string } {
  if (score === null) return { label: "offen", className: "bg-neutral-bg text-muted" };
  if (score >= 80) return { label: "hoch", className: "bg-success-bg text-success" };
  if (score >= 60) return { label: "mittel", className: "bg-info-bg text-info" };
  return { label: "niedrig", className: "bg-warning-bg text-warning" };
}

export function barTone(value: number): string {
  return value >= 70 ? "bg-accent" : value >= 50 ? "bg-[#8fb3dd]" : "bg-[#c2621a]";
}

/** „15/15“ oder ein Strich ohne Trigger-Test. */
export function webLabel(web: number[] | null): string {
  return web ? `${web[0]}/${web[1]}` : "–";
}

export function counts(items: readonly PromptItem[]): Record<PromptStatus, number> {
  const out: Record<PromptStatus, number> = { vorschlag: 0, freigegeben: 0, verworfen: 0 };
  for (const item of items) out[item.status] += 1;
  return out;
}

export function ofStatus(items: readonly PromptItem[], status: PromptStatus): PromptItem[] {
  return items.filter((item) => item.status === status);
}

/** Ob die Quelle ein Briefing braucht und ob es eines mit passendem Ergebnis gibt. */
export function briefingFor(source: string, briefing: PromptBriefing): boolean {
  if (source === "briefing") return briefing.prompts;
  if (source === "fanout") return briefing.fanouts;
  return source === "cluster" ? briefing.clusters : false;
}

export function needsBriefing(sources: readonly string[]): boolean {
  return sources.some((s) => (BRIEFING_SOURCES as readonly string[]).includes(s));
}

/** Geschätzte Kosten des Trigger-Tests für `count` Prompts (Euro). */
export function triggerEstimate(req: Pick<PromptRequirements, "trigger_eur_per_prompt">, count: number): number {
  return Math.round(req.trigger_eur_per_prompt * count * 10_000) / 10_000;
}

export function triggerLines(req: PromptRequirements, count: number): string[] {
  const estimate = triggerEstimate(req, count);
  const lines = [
    `${formatCount(count)} ${count === 1 ? "Prompt" : "Prompts"} × ${req.trigger_runs} Läufe × ${TRIGGER_PLATFORMS} Plattformen (ChatGPT, Gemini)`,
    `Geschätzte Kosten ca. ${formatEuro(estimate)} (DataForSEO-Guthaben)`,
  ];
  lines.push(
    req.validation_available
      ? "Markenerkennung in den Antworten: Gemini-Tokens (Cent-Bereich)"
      : "Ohne Gemini wird nur geprüft, ob deine Marke genannt wird, nicht welche anderen Marken vorkommen",
  );
  if (estimate > req.limit_per_job_eur) {
    lines.push(`Über dem Limit von ${formatEuro(req.limit_per_job_eur)} pro Job: Der Test wartet auf deine Freigabe.`);
  }
  return lines;
}

/** Kosten der gewählten Quellen als kurze Sätze (kostenlos wird nicht aufgezählt). */
/** Fan-outs des Topics liegen schon vor: nur ein neuer Abruf kostet DataForSEO-Guthaben. */
export function fanoutsStored(topic: PromptTopic | null | undefined): topic is PromptTopic & { fanouts_at: string } {
  return !!topic && !!topic.fanouts_at;
}

export function fanoutText(topic: PromptTopic & { fanouts_at: string }): string {
  return `gespeichert vom ${formatDate(topic.fanouts_at)} (${formatCount(topic.fanout_queries)} Fan-outs), kostenlos`;
}

export function generateCosts(
  req: PromptRequirements,
  selected: readonly string[],
  topic: PromptTopic | null = null,
  refreshFanouts = false,
): string[] {
  if (selected.length === 0) return [];
  const lines = req.sources
    .filter((s) => selected.includes(s.key) && !s.cost.startsWith("kostenlos"))
    .map((s) => {
      if (s.key === "fanout_live" && fanoutsStored(topic)) {
        return `${s.label}: ${refreshFanouts ? `neuer Abruf, ca. ${formatEuro(req.fanout_eur)} (DataForSEO)` : fanoutText(topic)}`;
      }
      return `${s.label}: ${s.cost}`;
    });
  if (req.validation_available) lines.push("Validierung: Gemini-Tokens (Cent-Bereich)");
  return lines;
}

type Trigger = NonNullable<PromptItem["trigger"]>;

/** Wie weit die Markenerkennung im Test lief: nie (älterer Test), nur die eigene Marke oder auch fremde Marken. */
export function brandCoverage(trigger: Trigger): "none" | "own" | "all" {
  if (!trigger.platforms.some((p) => p.answers > 0)) return "none";
  return trigger.platforms.some((p) => p.brands_checked > 0) ? "all" : "own";
}

/** „3/6“ für die eigene Marke oder ein Strich, wenn der Test keine Antworttexte hat. */
export function ownBrandLabel(platform: Trigger["platforms"][number]): string {
  if (platform.answers === 0) return "–";
  return platform.own_brand > 0 ? `ja (${platform.own_brand}/${platform.answers})` : "nein";
}

export type BrandRow = { name: string; count: string; detail: string };

/** Fremde Marken über beide Plattformen mit Zahl der Läufe und Aufteilung je Plattform. */
export function brandRows(trigger: Trigger): BrandRow[] {
  const total = trigger.platforms.reduce((sum, p) => sum + p.brands_checked, 0);
  const key = (name: string) => name.toLowerCase().replace(/\s+/g, " ").trim();
  return trigger.brands.map((brand) => ({
    name: brand.name,
    count: `${brand.runs}/${total}`,
    detail: trigger.platforms
      .map((p) => ({ p, hit: p.brands.find((b) => key(b.name) === key(brand.name)) }))
      .filter(({ hit }) => hit)
      .map(({ p, hit }) => `${p.label} ${hit!.runs}/${p.brands_checked}`)
      .join(", "),
  }));
}

// --- Funnel-Anteile beim Generieren ----------------------------------------------------------------

export const FUNNEL_STAGES: PromptFunnel[] = ["awareness", "consideration", "decision", "retention"];
export type FunnelMix = Record<PromptFunnel, number>;
export const EVEN_MIX: FunnelMix = { awareness: 25, consideration: 25, decision: 25, retention: 25 };

export function mixTotal(mix: FunnelMix): number {
  return FUNNEL_STAGES.reduce((sum, stage) => sum + mix[stage], 0);
}

/** Text unter den Eingaben: Summe und was zu 100 % fehlt. Kein Fehlertext allein über Farbe. */
export function mixHint(mix: FunnelMix): string {
  const total = mixTotal(mix);
  if (total === 100) return "Zusammen 100 %";
  if (total < 100) return `Zusammen ${total} %: Es fehlen noch ${100 - total} %`;
  return `Zusammen ${total} %: ${total - 100} % zu viel`;
}

export function formatMix(mix: Record<string, number>): string {
  return FUNNEL_STAGES.map((stage) => `${FUNNEL_LABEL[stage]} ${formatCount(mix[stage] ?? 0)} %`).join(", ");
}

// --- Sortieren und Filtern der Liste -----------------------------------------------------------------

export type SortKey = "text" | "tag" | "funnel" | "kind" | "web" | "score";
export type Sort = { key: SortKey; dir: "asc" | "desc" } | null;
/** Wert des Tag-Filters für Prompts ohne Cluster. */
export const NO_TAG = "__none";

export type Filters = {
  text: string;
  tag: string;
  funnel: "" | PromptFunnel | "none";
  kind: "" | "branded" | "unbranded" | "none";
  web: "" | "untested" | "all" | "some" | "none";
  score: "" | "high" | "mid" | "low" | "open";
};
export const NO_FILTERS: Filters = { text: "", tag: "", funnel: "", kind: "", web: "", score: "" };

export const WEB_FILTER_LABEL: Record<Exclude<Filters["web"], "">, string> = {
  untested: "Nicht getestet",
  all: "In allen Läufen",
  some: "In einigen Läufen",
  none: "In keinem Lauf",
};
export const SCORE_FILTER_LABEL: Record<Exclude<Filters["score"], "">, string> = {
  high: "Hoch (80 und mehr)",
  mid: "Mittel (60 bis 79)",
  low: "Niedrig (unter 60)",
  open: "Offen (ohne Score)",
};

/** Nächster Zustand beim Klick auf eine Spaltenüberschrift: aufsteigend, absteigend, aus. */
export function nextSort(current: Sort, key: SortKey): Sort {
  if (current?.key !== key) return { key, dir: "asc" };
  return current.dir === "asc" ? { key, dir: "desc" } : null;
}

export function activeFilterCount(filters: Filters): number {
  return Object.values(filters).filter((value) => value.trim() !== "").length;
}

function matchesWeb(item: PromptItem, filter: Filters["web"]): boolean {
  if (filter === "") return true;
  if (!item.web) return filter === "untested";
  const [web, runs] = item.web;
  if (filter === "untested") return false;
  if (filter === "all") return runs > 0 && web === runs;
  if (filter === "none") return runs > 0 && web === 0;
  return web > 0 && web < runs;
}

function matchesScore(item: PromptItem, filter: Filters["score"]): boolean {
  if (filter === "") return true;
  if (item.score === null) return filter === "open";
  if (filter === "open") return false;
  if (filter === "high") return item.score >= 80;
  return filter === "mid" ? item.score >= 60 && item.score < 80 : item.score < 60;
}

export function filterItems(items: readonly PromptItem[], filters: Filters): PromptItem[] {
  const needle = filters.text.trim().toLowerCase();
  return items.filter(
    (item) =>
      (needle === "" || item.text.toLowerCase().includes(needle)) &&
      (filters.tag === "" || (filters.tag === NO_TAG ? item.tag === null : item.tag === filters.tag)) &&
      (filters.funnel === "" || (filters.funnel === "none" ? item.funnel === null : item.funnel === filters.funnel)) &&
      (filters.kind === "" || (filters.kind === "none" ? item.kind === null : item.kind === filters.kind)) &&
      matchesWeb(item, filters.web) &&
      matchesScore(item, filters.score),
  );
}

function sortValue(item: PromptItem, key: SortKey): string | number | null {
  switch (key) {
    case "text":
      return item.text;
    case "tag":
      return item.tag;
    case "funnel":
      return item.funnel ? FUNNEL_STAGES.indexOf(item.funnel) : null;
    case "kind":
      return item.kind;
    case "web":
      return item.web && item.web[1] > 0 ? item.web[0] / item.web[1] : null;
    default:
      return item.score;
  }
}

/** Sortiert stabil. Prompts ohne Wert stehen in beiden Richtungen am Ende. */
export function sortItems(items: readonly PromptItem[], sort: Sort): PromptItem[] {
  if (!sort) return [...items];
  const factor = sort.dir === "asc" ? 1 : -1;
  return [...items].sort((a, b) => {
    const left = sortValue(a, sort.key);
    const right = sortValue(b, sort.key);
    if (left === null || right === null) return left === right ? 0 : left === null ? 1 : -1;
    if (typeof left === "string" && typeof right === "string") return factor * left.localeCompare(right, "de");
    return factor * (Number(left) - Number(right));
  });
}

/** Die Tags der Prompts mit Zahl, größte Cluster zuerst (Auswahl des Tag-Filters). */
export function tagOptions(items: readonly PromptItem[]): { tag: string; count: number }[] {
  const counts = new Map<string, number>();
  for (const item of items) if (item.tag) counts.set(item.tag, (counts.get(item.tag) ?? 0) + 1);
  return [...counts.entries()].map(([tag, count]) => ({ tag, count })).sort((a, b) => b.count - a.count || a.tag.localeCompare(b.tag, "de"));
}

// --- Gespeicherte Läufe ------------------------------------------------------------------------------

export const RUN_KIND_LABEL: Record<PromptRun["kind"], string> = {
  generate: "Generierung",
  cluster: "Clustering",
  demand: "Nachfrage",
  validate: "Validierung",
  trigger: "Trigger-Test",
};

export const RUN_STATUS_LABEL: Record<string, string> = {
  queued: "Wartet auf den Worker",
  running: "Läuft",
  awaiting_approval: "Wartet auf Freigabe",
  succeeded: "Fertig",
  failed: "Fehlgeschlagen",
  cancelled: "Abgebrochen",
};

const RUN_TITLE: Record<PromptRun["kind"], string> = {
  generate: "Prompts werden generiert",
  cluster: "Prompts werden geclustert",
  demand: "Nachfrage wird ermittelt",
  validate: "Prompts werden validiert",
  trigger: "Trigger-Test läuft",
};

export function runTitle(run: Pick<PromptRun, "kind">): string {
  return RUN_TITLE[run.kind];
}

/** Läuft noch oder wartet: so ein Lauf wird beim Öffnen der Seite wieder angezeigt. */
export function runIsOpen(run: Pick<PromptRun, "status">): boolean {
  return run.status === "queued" || run.status === "running" || run.status === "awaiting_approval";
}

/** Die Vorgaben des Laufs in einer Zeile: Quellen, Funnel-Anteile, Keyword oder Zahl der Prompts. */
export function runSettings(run: PromptRun): string {
  const parts: string[] = [];
  if (run.sources.length > 0) parts.push(`Quellen: ${run.sources.join(", ")}`);
  if (run.funnel_mix) parts.push(`Funnel: ${formatMix(run.funnel_mix)}`);
  if (run.seed) parts.push(`Keyword „${run.seed}“`);
  if (run.kind !== "generate" && run.prompt_count) parts.push(`${formatCount(run.prompt_count)} ${run.prompt_count === 1 ? "Prompt" : "Prompts"}`);
  return parts.join(" · ") || "–";
}

/** Zeilen der Rückfrage vor dem Clustern: Umfang und Kosten (Gemini rechnet in Tokens). */
export function clusterLines(count: number): string[] {
  return [
    `${formatCount(count)} ${count === 1 ? "Prompt" : "Prompts"} werden in Cluster geordnet, jedes Cluster bekommt einen Tag`,
    "Kosten: Gemini-Tokens (Cent-Bereich), keine Euro-Kosten",
  ];
}

/** Obergrenze der ahrefs-Units: zwei Abrufe (Suchbegriffe, Parent Topics), je Abruf mindestens 50. */
export function demandEstimate(req: Pick<PromptRequirements, "demand_units_per_prompt" | "demand_units_minimum">, count: number): number {
  return count <= 0 ? 0 : Math.max(req.demand_units_minimum, req.demand_units_per_prompt * count);
}

/** Zeilen der Rückfrage vor der Nachfrage-Ermittlung: Umfang, Weg und Kosten. */
export function demandLines(req: PromptRequirements, count: number): string[] {
  return [
    `${formatCount(count)} ${count === 1 ? "Prompt" : "Prompts"} ohne Suchvolumen`,
    `Kosten: bis zu ${formatCount(demandEstimate(req, count))} ahrefs-Units (Abo, keine Euro-Kosten) und Gemini-Tokens (Cent-Bereich)`,
  ];
}

/** Anteil 0 bis 1 als „25 %“, ohne Daten ein Strich. */
export function formatShare(share: number | null): string {
  return share === null ? "–" : `${Math.round(share * 100).toLocaleString("de-DE")} %`;
}

/** Vorschlag für das Haupt-Keyword eines Briefings aus einem Cluster: Topic und Tag ohne doppelte Wörter. */
export function clusterKeyword(topic: string, tag: string): string {
  const words = topic.toLowerCase().split(/\s+/).filter(Boolean);
  for (const word of tag.toLowerCase().split(/\s+/).filter(Boolean)) if (!words.includes(word)) words.push(word);
  return words.join(" ").slice(0, 200);
}
